/**
 * App config feature-flag hooks (appConfig + initial cache).
 * For API/org React Query hooks, import from `data/domains/config`.
 */
import { useState, useEffect } from 'react';
import {
  isCommunityFeatureEnabled,
  isJourneyEnabled,
  isJourneyByApiEnabled,
  isConsultationsFeatureEnabled,
  isOnlineOrdersFeatureEnabled,
  getSubscriptionListStructure,
  getSubscriptionListStyle,
  isShowSubscriptionScaledMacrosEnabled,
  isShowCustomPlansEnabled,
  isSaveMenuFlowEnabled,
  getDashboardVariant,
  getCalendarVariant,
  getMenuFlowVariant,
  getAccountsVariant,
  getMainScreenVariant,
  getAllergyPickerMode,
  isLoggedOutRamadanMenuEnabled,
  isShowLogoClassicEnabled,
  isBannerLoopEnabled,
  getGuestFlowType,
  getGuestMenuListMode,
  getGuestMenuSize,
  getPortionTextMode,
  isShowPortionEnabled,
  isScaledTextVisible,
  isCaloriesLimitVisible,
  getCaloriesLimitColorsMode,
  resolveMenuTabFlowFromMenuPageStyle,
  isFoodMenuTabFlow,
  type DashboardVariant,
  type MainScreenVariant,
  type AllergyPickerMode,
  type GuestFlowType,
  type GuestMenuListMode,
  type PortionTextMode,
  type SubscriptionListStructure,
  type SubscriptionListStyle,
} from './appConfig';
import type { MenuTabFlowKind } from '../../../shared/navigation/menuTabResolver';
import { fetchInitialConfig } from './initialApi';
import { isLoyaltyFeatureEnabled } from './organizationFeatures';
import { isNutritionUiEnabled } from './nutritionUi';

function useAppConfigBooleanFlag(read_flag: () => boolean): boolean {
  const [enabled, set_enabled] = useState(read_flag);
  useEffect(() => {
    let alive = true;
    fetchInitialConfig().finally(() => {
      if (alive) {
        set_enabled(read_flag());
      }
    });
    return () => {
      alive = false;
    };
  }, []);
  return enabled;
}

function useAppConfigEnumFlag<T extends string>(read_flag: () => T): T {
  const [value, set_value] = useState(read_flag);
  useEffect(() => {
    let alive = true;
    fetchInitialConfig().finally(() => {
      if (alive) {
        set_value(read_flag());
      }
    });
    return () => {
      alive = false;
    };
  }, []);
  return value;
}

export function useCommunityFeatureEnabled(): boolean {
  return useAppConfigBooleanFlag(isCommunityFeatureEnabled);
}

export function useJourneyFeatureEnabled(): boolean {
  return useAppConfigBooleanFlag(isJourneyEnabled);
}

export function useJourneyByApiEnabled(): boolean {
  return useAppConfigBooleanFlag(isJourneyByApiEnabled);
}

export function useConsultationsFeatureEnabled(): boolean {
  return useAppConfigBooleanFlag(isConsultationsFeatureEnabled);
}

export function useOnlineOrdersFeatureEnabled(): boolean {
  return useAppConfigBooleanFlag(isOnlineOrdersFeatureEnabled);
}

export function useLoyaltyFeatureEnabled(): boolean {
  const [enabled, set_enabled] = useState(isLoyaltyFeatureEnabled);
  useEffect(() => {
    let alive = true;
    fetchInitialConfig().finally(() => {
      if (alive) {
        set_enabled(isLoyaltyFeatureEnabled());
      }
    });
    return () => {
      alive = false;
    };
  }, []);
  return enabled;
}

export function useSubscriptionListStructure(): SubscriptionListStructure {
  return useAppConfigEnumFlag(getSubscriptionListStructure);
}

export function useSubscriptionListStyle(): SubscriptionListStyle {
  return useAppConfigEnumFlag(getSubscriptionListStyle);
}

export function useShowSubscriptionScaledMacrosEnabled(): boolean {
  return isShowSubscriptionScaledMacrosEnabled();
}

export function useShowCustomPlansEnabled(): boolean {
  return isShowCustomPlansEnabled();
}

export function useSaveMenuFlowEnabled(): boolean {
  return useAppConfigBooleanFlag(isSaveMenuFlowEnabled);
}

export function useDashboardVariant(): DashboardVariant {
  return useAppConfigEnumFlag(getDashboardVariant);
}

export function useCalendarVariant(): DashboardVariant {
  return useAppConfigEnumFlag(getCalendarVariant);
}

export function useMenuFlowVariant(): DashboardVariant {
  return useAppConfigEnumFlag(getMenuFlowVariant);
}

export function useAccountsVariant(): DashboardVariant {
  return useAppConfigEnumFlag(getAccountsVariant);
}

export function useMainScreenVariant(): MainScreenVariant {
  return useAppConfigEnumFlag(getMainScreenVariant);
}

export function useAllergyPickerMode(): AllergyPickerMode {
  return useAppConfigEnumFlag(getAllergyPickerMode);
}

/** Show Ramadan/futoor/suhoor food types on logged-out Main → menu browse when `logged_out_ramadan_menu` is true. */
export function useLoggedOutRamadanMenuEnabled(): boolean {
  return useAppConfigBooleanFlag(isLoggedOutRamadanMenuEnabled);
}

/** Classic main: show client logo SVG when `show_logo_classic` is true. */
export function useShowLogoClassicEnabled(): boolean {
  return useAppConfigBooleanFlag(isShowLogoClassicEnabled);
}

/** Classic main banner video/YouTube loop when `banner_loop` is true (default). */
export function useBannerLoopEnabled(): boolean {
  return useAppConfigBooleanFlag(isBannerLoopEnabled);
}

export function useGuestFlowType(): GuestFlowType {
  return useAppConfigEnumFlag(getGuestFlowType);
}

export function useGuestMenuListMode(): GuestMenuListMode {
  return useAppConfigEnumFlag(getGuestMenuListMode);
}

/** Logged-out guest menu browse window (`guest_menu_size`). Default / fallback: 7. */
export function useGuestMenuSize(): number {
  const [size, set_size] = useState(getGuestMenuSize);
  useEffect(() => {
    let alive = true;
    fetchInitialConfig().finally(() => {
      if (alive) {
        set_size(getGuestMenuSize());
      }
    });
    return () => {
      alive = false;
    };
  }, []);
  return size;
}

export function usePortionTextMode(): PortionTextMode {
  return useAppConfigEnumFlag(getPortionTextMode);
}

/** Subscription card portion/persons gate (`show_portion`). */
export function useShowPortionEnabled(): boolean {
  return useAppConfigBooleanFlag(isShowPortionEnabled);
}

/** @deprecated Parsed only — does not gate UI. Prefer `useNutritionUiEnabled`. */
export function useScaledTextVisible(): boolean {
  return useAppConfigBooleanFlag(isScaledTextVisible);
}

/** Subscription card calorie min–max gate (`calories_limit`). */
export function useCaloriesLimitVisible(): boolean {
  return useAppConfigBooleanFlag(isCaloriesLimitVisible);
}

export function useCaloriesLimitColorsMode(): string {
  const [mode, set_mode] = useState(getCaloriesLimitColorsMode);
  useEffect(() => {
    let alive = true;
    fetchInitialConfig().finally(() => {
      if (alive) {
        set_mode(getCaloriesLimitColorsMode());
      }
    });
    return () => {
      alive = false;
    };
  }, []);
  return mode;
}

/** Driven by `app_config.is_nutrition_ui_visible` (default true). */
export function useNutritionUiEnabled(): boolean {
  return useAppConfigBooleanFlag(isNutritionUiEnabled);
}

export function useMenuTabFlow(): MenuTabFlowKind {
  return useAppConfigEnumFlag(resolveMenuTabFlowFromMenuPageStyle);
}

/** True when menu tab uses foodMenu domain (menu_page_style type3 / type4). */
export function useIsFoodMenuTabFlow(): boolean {
  return useAppConfigBooleanFlag(isFoodMenuTabFlow);
}

/** @deprecated use useMenuTabFlow */
export const useMenuTabFlowKind = useMenuTabFlow;
